// @flow
import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Input,
  AutoComplete,
  Select,
} from 'antd';
import SimpleReactValidator from 'simple-react-validator';
import getCountryPhoneCode from 'utils/getCountryPhoneCode';
import { countryCodeValidation, validators } from 'utils/validationMessages';
import { isValidPhoneNumber, formatNumber, findPhoneNumbersInText } from 'libphonenumber-js';
import getVendorType from 'utils/getVendorType';
import getSortedList from 'utils/getSortedList';
import getCountryAlpha2 from 'utils/getCountryAlpha2';
import getStateList from 'utils/stateList';
import { SpH5, SpError } from 'components/DesignKit';
import {
  COUNTRY_PHONE_CODE,
  COUNTRY_CODES,
  BENE_TYPES,
} from 'appconstants';

const { Option } = Select;

type Props = {
  submitting: boolean,
  selectedAccount: Array<Object>,
  contactDetailsData: Object,
  transferDetailsData: Object,
  fetchContactDetails: Function,
  statesInCountry: Array<Object>,
  zipTag: String,
  cntryId: Number,
  fetchFundingSourceDetails: Function,
  clearFundingSource: Function,
};

const ContactDetails = (props: Props, ref) => {
  const {
    submitting,
    contactDetailsData,
    selectedAccount,
    fetchContactDetails,
    statesInCountry,
    zipTag,
    cntryId,
    fetchFundingSourceDetails,
    transferDetailsData,
    clearFundingSource,
  } = props;
  const [, forceUpdate] = useState();
  const [contactName, setContactName] = useState(contactDetailsData.contactName);
  const [email, setEmail] = useState(contactDetailsData.email);
  const [contactGid, setContactGid] = useState(contactDetailsData.contactGid);
  const [contactPhone, setContactPhone] = useState(contactDetailsData.contactPhone);
  const [customType, setCustomType] = useState(contactDetailsData.customType || '');
  // eslint-disable-next-line no-unused-vars
  const [customSuggestions, setCustomSuggestions] = useState(contactDetailsData.customSuggestions || []);
  // eslint-disable-next-line max-len
  const [phoneNumberCode, setPhoneCode] = useState(contactDetailsData.phoneNumberCode ? contactDetailsData.phoneNumberCode : getCountryPhoneCode(selectedAccount.country.id));
  const [phoneFormate, setPhoneFormate] = useState(contactDetailsData.phoneFormate);
  const [contactType, setContactType] = useState(contactDetailsData.contactType || 'CUSTOMER');
  const [contactTypeName, setContactTypeName] = useState(contactDetailsData.contactTypeName || 'Customer');
  const contacts = contactDetailsData.contactResult;
  const [disableContact, setDisableContact] = useState(contactDetailsData.disableContact);
  const [countryCode, setCountryCode] = useState(contactDetailsData.countryCode ? contactDetailsData.countryCode : getCountryAlpha2(cntryId));
  const [streetName, setStreetName] = useState(contactDetailsData.streetName);
  const [cityName, setCityName] = useState(contactDetailsData.cityName);
  const [stateName, setStateName] = useState(contactDetailsData.stateName);
  const [zipCode, setZipCode] = useState(contactDetailsData.zipCode);
  const [countryName, setCountryName] = useState(contactDetailsData.countryCode && contactDetailsData.countryCode === 'IN' ? 'INDIA' : 'USA');
  const [stateList, setSateList] = useState((contactDetailsData.stateList) || (statesInCountry));

  const { transferType } = transferDetailsData;

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      phoneNumValidation: {
        message: validators.register.validPhone,
        rule: (val, param) => (isValidPhoneNumber(param[0])),
      },
      checkLowerCase: {
        message: 'Email address must not contain uppercase letters.',
        rule: (val) => val === val.toLowerCase(),
      },
    },
  }));

  // useEffect(() => {
  //   if (!contactDetailsData.countryCode && country && country === 'India') {
  //     setCountryName('INDIA');
  //   } else if (!contactDetailsData.countryCode && country && country === 'United States of America') {
  //     setCountryName('USA');
  //   }
  // }, []);

  useEffect(() => {
    if (contactGid) {
      let beneType;
      if (transferType === BENE_TYPES.UPI) {
        beneType = 'issuerBank.vpa.NULL=false';
      } else if (transferType === BENE_TYPES.NEFT || transferType === BENE_TYPES.RTGS || transferType === BENE_TYPES.IMPS) {
        beneType = 'issuerBank.routingNumber.NULL=false';
      }
      const searchParam = `page=1&size=100&contact.gid.EQ=${contactGid}&${beneType}`;
      fetchFundingSourceDetails(searchParam);
    }
  }, [transferType]);

  useEffect(() => {
    simpleValidator.current.purgeFields();
  }, [disableContact]);

  const updateCountry = (value) => {
    setCountryName(value === 'IN' ? 'INDIA' : (value === 'US' ? 'USA' : 'CANADA'));
    setCountryCode(value);
    setSateList(getStateList(value === 'IN' ? 2 : (value === 'US' ? 1 : 41)));
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const formValid = simpleValidator.current.allValid();
      if (!formValid) {
        simpleValidator.current.showMessages();
        forceUpdate(1);
        return false;
      }
      return true;
    },
    getValues: () => ({
      contactName,
      contactGid,
      email,
      phoneNumberCode,
      contactPhone,
      phoneFormate,
      contactType,
      customType,
      contactTypeName,
      disableContact,
      countryCode,
      streetName,
      cityName,
      stateName,
      zipCode,
      countryName,
    }),
  }));

  const searchContact = (value) => {
    fetchContactDetails(value);
    setContactName(value);
    const results = contacts.filter((item) => (item.name === value));
    if (results.length === 0) {
      setDisableContact(false);
      setContactGid('');
      clearFundingSource();
    }
  };

  const onPhoneNumberChange = (e) => {
    const updatedPhone = e.currentTarget.value;
    setContactPhone(updatedPhone.replace(/[^0-9+]/g, ''));
    setPhoneFormate(updatedPhone.replace(/[^0-9+]/g, ''));
  };

  const onCountryCodeChange = (value) => {
    setPhoneCode(value);
    const data = formatNumber(`${value}${contactPhone}`, 'INTERNATIONAL');
    const formater = data.substr(data.indexOf(' ') + 1);
    setPhoneFormate(formater);
  };

  const updateContact = (value) => {
    const results = contacts.filter((item) => (item.gid === value));
    if (contactGid !== results[0].gid) {
      if (results && results[0].gid) {
        let beneType;
        if (transferType === BENE_TYPES.UPI) {
          beneType = 'issuerBank.vpa.NULL=false';
        } else if (transferType === BENE_TYPES.NEFT || transferType === BENE_TYPES.RTGS || transferType === BENE_TYPES.IMPS) {
          beneType = 'issuerBank.routingNumber.NULL=false';
        }
        const searchParam = `page=1&size=100&contact.gid.EQ=${results[0].gid}&${beneType}`;
        fetchFundingSourceDetails(searchParam);
      }
      setEmail(results[0].email);
      setContactName(results[0].name);
      setContactGid(results[0].gid);
      setContactType(results[0].contactType);
      setCustomType(results[0].customType);
      setContactTypeName(getVendorType(results[0].contactType));
      setDisableContact(true);
      setStreetName(results[0].address && results[0].address.street);
      setCityName(results[0].address && results[0].address.city);
      setStateName(results[0].address && results[0].address.state);
      setZipCode(results[0].address && results[0].address.postalCode);
      setSateList(getStateList(results[0].address && results[0].address && results[0].address.country.id));
      if (((results[0].address) && (results[0].address.country.id)) === 1) {
        setCountryName('USA');
      } else if (((results[0].address) && (results[0].address.country.id)) === 2) {
        setCountryName('INDIA');
      }
      setCountryCode(getCountryAlpha2(results[0].address && results[0].address.country.id));
      if (results[0].phoneNumber) {
        const phoneResult = results[0].phoneNumber ? findPhoneNumbersInText(results[0].phoneNumber) : null;
        if ((phoneResult && phoneResult.length > 0) && phoneResult[0].number) {
          setPhoneCode(`+${phoneResult[0].number.countryCallingCode}`);
          setContactPhone(phoneResult[0].number.nationalNumber);
          const data = formatNumber(phoneResult[0].number.number, 'INTERNATIONAL');
          const formater = data.substr(data.indexOf(' ') + 1);
          setPhoneFormate(formater);
        }
      }
    }
  };

  return (
    <>
      <div>
        <div className={submitting ? 'OVERLAY' : ''} />
        <div className="mt-2 mb-3">
          <label htmlFor=""><SpH5>Name</SpH5></label>
          <AutoComplete
            placeholder="Select Contact Name"
            showSearch
            className="w-100"
            dataSource={getSortedList(contacts).map((item, id) => (
              <Option key={id} value={item.gid}>
                {item.name}
              </Option>
            ))}
            style={{ height: '40px' }}
            onSearch={searchContact}
            defaultValue={contactName}
            onSelect={e => updateContact(e)}
          />
          <SpError>
            {simpleValidator.current.message('name', contactName, 'required')}
          </SpError>
        </div>

        <div className="mb-3">
          <label htmlFor=""><SpH5>Email</SpH5></label>
          <Input
            placeholder="Contact Email"
            value={email}
            disabled={disableContact}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          {
            !disableContact && (
              <SpError>
                {simpleValidator.current.message('email', email, 'required|email|checkLowerCase')}
              </SpError>
            )
          }
        </div>

        <div className="mb-3">
          <label htmlFor=""><SpH5>Phone</SpH5></label>
          <div className="d-flex">
            <div
              style={{ width: '20%' }}
              onKeyPress={e => countryCodeValidation(e)}
            >
              <Select
                className="w-100"
                showSearch
                value={phoneNumberCode}
                onChange={onCountryCodeChange}
                disabled={disableContact}
              >
                {COUNTRY_PHONE_CODE.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </div>
            <div style={{ width: '80%' }}>
              <Input
                className="InputnumFormate"
                placeholder="Phone Number"
                value={phoneFormate}
                disabled={disableContact}
                onChange={onPhoneNumberChange}
                onBlur={e => {
                  if (e.currentTarget.value) {
                    setContactPhone(e.currentTarget.value);
                    const value = formatNumber(`${phoneNumberCode}${e.currentTarget.value}`, 'INTERNATIONAL');
                    const formater = value.substr(value.indexOf(' ') + 1);
                    setPhoneFormate(formater);
                  }
                }}
              />
              {
                !disableContact && (
                  <SpError>
                    {simpleValidator.current.message('phone number', contactPhone, `required|phoneNumValidation:${phoneNumberCode}${contactPhone}`)}
                  </SpError>
                )
              }
            </div>
          </div>
        </div>
        <div className="mb-3">
          <label htmlFor=""><SpH5>Contact Type</SpH5></label>
          <Select
            className="w-100"
            showSearch
            value={contactTypeName}
            disabled={disableContact}
            onChange={(value) => {
              setContactType(value[0]);
              setContactTypeName(value[1]);
            }}
          >
            <Option key={1} value={['CUSTOMER', 'Customer']}>Customer</Option>
            <Option key={2} value={['EMPLOYEE', 'Employee']}>Employee</Option>
            <Option key={3} value={['VENDOR', 'Vendor']}>Vendor</Option>
            <Option key={4} value={['SELF', 'Self']}>Self</Option>
            <Option key={5} value={['OTHER', 'Other']}>Other</Option>
          </Select>
          <SpError>
            {simpleValidator.current.message('contact Type', contactType, 'required')}
          </SpError>
        </div>
        {contactType && contactType === 'OTHER' && (
          <div className="mb-3">
            <label htmlFor="">
              <SpH5>Custom Type</SpH5>
            </label>
            {customSuggestions.length > 0 ? (
              <>
                <Select
                  showSearch
                  placeholder="(Optional)"
                  className="w-100 mb-2"
                  value={customType}
                  onChange={(value) => setCustomType(value)}
                  onSearch={(value) => setCustomType(value)} // Handle typing a custom type
                  mode="combobox" // Allows users to input a value not in the list
                >
                  {customSuggestions.map((word, index) => (
                    <Select.Option key={index} value={word}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{word}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </>
            ) : (
              <Input
                placeholder="(Optional)"
                value={customType || ''}
                onChange={(e) => setCustomType(e.target.value)}
              />
            )}
          </div>
        )}
        <div className="mb-3">
          <label htmlFor=""><SpH5>Street Address</SpH5></label>
          <Input
            placeholder="Street Address"
            value={streetName}
            disabled={disableContact}
            onChange={(e) => setStreetName(e.currentTarget.value)}
          />
          {
            !disableContact && (
              <SpError>
                {simpleValidator.current.message('street', streetName, 'required')}
              </SpError>
            )
          }
        </div>
        <div className="pb-3">
          <div className="d-flex">
            <div className="w-50 mr-3">
              <label className="d-block"><SpH5>City</SpH5></label>
              <Input
                placeholder="City"
                disabled={disableContact}
                value={cityName}
                onChange={(e) => setCityName(e.currentTarget.value)}
              />
              {
                !disableContact && (
                  <SpError>
                    {simpleValidator.current.message('city', cityName, 'required')}
                  </SpError>
                )
              }
            </div>
            <div className="w-50">
              <label className="d-block"><SpH5>State</SpH5></label>
              <Select
                className="w-100"
                disabled={disableContact}
                showSearch
                value={stateName || 'Select State'}
                optionFilterProp="children"
                onChange={(e) => setStateName(e)}
              >
                {
                  stateList.map(s => (
                    <Option
                      key={s.id}
                      value={s.id}
                      className="text-capitalize"
                    >
                      {s.name}
                    </Option>
                  ))
                }
              </Select>
              {
                !disableContact && (
                  <SpError>
                    {simpleValidator.current.message('state', stateName, 'required')}
                  </SpError>
                )
              }
            </div>
          </div>
        </div>

        <div className="pb-3">
          <div className="d-flex">
            <div className="w-50 mr-3">
              <label className="d-block"><SpH5>{zipTag}</SpH5></label>
              <Input
                placeholder={zipTag}
                value={zipCode}
                disabled={disableContact}
                maxLength={countryCode === 'IN' ? 6 : 5}
                minLength={5}
                onChange={(e) => setZipCode(e.currentTarget.value)}
              />
              {
                !disableContact && (
                  <SpError>
                    {simpleValidator.current.message(zipTag, zipCode, 'required|numeric')}
                  </SpError>
                )
              }
            </div>
            <div className="w-50">
              <label className="d-block"><SpH5>Country</SpH5></label>
              <Select
                value={countryName}
                disabled={disableContact || !selectedAccount.allowCrossBorderTransfer}
                style={{ width: '100%' }}
                onChange={updateCountry}
              >
                {COUNTRY_CODES.map((item) => (
                  <Option key={item.ID} value={item.ALPHA2}>
                    {item.NAME}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// $FlowFixMe
export default forwardRef(ContactDetails);
